import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as Sentry from '@sentry/react';
import * as modalActions from '../../modules/modal/actions';
import { cancelSubscription, clickUserProfileLogout } from './actions';
import ChangePasswordModal, { CHANGE_PASSWORD_MODAL_NAME } from './ChangePasswordModal';
import ChangeWordsGoalModal, { CHANGE_WORDS_GOAL_MODAL_NAME } from './ChangeWordsGoalModal';
import MembershipSettings from '../../components/MembershipSettings';
import ImportExport from '../../components/ProfileBackup';
import ProfileSettings from '../../components/ProfileSettings';
import SecurityAndPrivacy from '../../components/SecurityAndPrivacy';
import { getUserProfile } from './selectors';
import { selectSyncLogs } from '../PagesHistory/AuditLog/selectors';
import { useIntl } from 'react-intl';
import Button from '../../components/Button';
import { useCallback } from 'react';

const UserProfileScrollContainer = styled.div`
  flex-grow: 1;
  overflow-y: scroll;
  max-width: 100vw;
  max-height: calc(100vh - ${p => p.theme.sizes.headerHeight} - ${p => p.theme.sizes.headerHeight});
`;

const UserProfileContainer = styled.div`
  flex-grow: 1;
  max-width: 540px;
  margin: 0 auto;
`;

const UserProfileSection = styled.div`
  padding: 2rem 0;

  border-top: 1px solid ${p => p.theme.colorLightGrey};

  &:first-child {
    border-top: none;
  }
`;

const IssueReporter = () => {
  const appState = useSelector(state => {
    return state;
  });

  const reportBug = useCallback(() => {
    const state = JSON.parse(JSON.stringify(appState));

    // tslint:disable-next-line:forin
    for (const pageId in state.pagesHistory.items) {
      const page = state.pagesHistory.items[pageId];

      page.text = `${page.text.length}`;
    }

    state.localeSettings = undefined;

    Sentry.addBreadcrumb({
      data: {
        state: JSON.stringify(state),
      },
    });

    const eventId = Sentry.captureMessage('Bug reported by user');

    Sentry.showReportDialog({
      eventId,
      user: {
        email: state.userProfile.userInfo.email,
        name: state.userProfile.userInfo.email,
      },
    });
  }, []);

  return (
    <div>
      <Button onClick={reportBug}>Report bug</Button>
    </div>
  );
};

const UserProfile = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector(getUserProfile).userInfo;
  const syncLogs = useSelector(selectSyncLogs);
  const intl = useIntl();

  if (!userInfo) {
    Sentry.captureException(new Error('Undefined user session for logged in user'));
    return <div />;
  }

  const handlePasswordChangeClick = () => {
    dispatch(modalActions.openModal(CHANGE_PASSWORD_MODAL_NAME));
  };

  const handleWordsGoalChangeClick = () => {
    dispatch(modalActions.openModal(CHANGE_WORDS_GOAL_MODAL_NAME));
  };

  const handleLogOutClick = () => {
    const syncingData = syncLogs.logs.filter(log => (log.meta.needSync = true));
    if (syncingData.length > 0) {
      const shouldLogout = confirm(
        intl.formatMessage({
          id: 'containers.UserProfile.logout',
        }),
      );

      if (shouldLogout) {
        dispatch(clickUserProfileLogout());
      }
    } else {
      dispatch(clickUserProfileLogout());
    }
  };

  const handleCancelSubscription = () => {
    dispatch(cancelSubscription());
  };

  return (
    <UserProfileScrollContainer>
      <UserProfileContainer>
        <UserProfileSection>
          <ProfileSettings
            onLogout={handleLogOutClick}
            onPasswordChangeClick={handlePasswordChangeClick}
            onWordsGoalChangeClick={handleWordsGoalChangeClick}
          />
        </UserProfileSection>
        <UserProfileSection>
          <MembershipSettings userInfo={userInfo} onCancelSubscription={handleCancelSubscription} />
        </UserProfileSection>
        <UserProfileSection>
          <ImportExport canImportFrom750={true} canImportFromDayOne={true} />
        </UserProfileSection>
        <UserProfileSection>
          <SecurityAndPrivacy />
        </UserProfileSection>
        <UserProfileSection>
          <IssueReporter />
        </UserProfileSection>
        <ChangePasswordModal />
        <ChangeWordsGoalModal />
      </UserProfileContainer>
    </UserProfileScrollContainer>
  );
};

export default React.memo(UserProfile);



// WEBPACK FOOTER //
// ./src/containers/UserProfile/index.tsx